import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 px-3" }
const _hoisted_5 = { class: "d-flex align-items-center gap-2" }
const _hoisted_6 = ["value"]
const _hoisted_7 = { class: "pagination-mini" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled", "onClick"]
const _hoisted_10 = ["disabled"]

import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue"; 
import { usePermissions } from "@/composables/usePermissions";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import apiService from "@/services/apiService";
import { toast, type Content } from "vue3-toastify";
import ProcessFlowEditor from "./ProcessFlowEditor.vue";

const API_RESOURCE = "cms-process-flow/process-transitions";
const pageTitle = "Process Transition";
const title = "Default Processes";


export default /*@__PURE__*/_defineComponent({
  __name: 'DefaultProcess',
  setup(__props) {

const { canCreate } = usePermissions();
const crud = useCrud(API_RESOURCE);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [1];
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

const rows = ref<any[]>([]);
const cardTypes = ref<any[]>([]);
const processGroups = ref<any[]>([]);

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Card Type", field: "type_value", sortable: true, width: "auto" }
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",  
});

watch(crud.data, (newVal) => rows.value = newVal || []);
watch(crud.isLoading, (v) => tableConfig.value.loading = v);
watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

const changeRows = () => {
  currentPage.value = 1;
  crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};
const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) return;
  currentPage.value = page;
  crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);
const isFlowEditorOpen = ref(false);
const selectedProcess = ref<any>(null);

const newItemData = ref({ 
  group_id: "", 
  title: "", 
  variable_type: "card_type", 
  type_value: "" 
});

const editingItemData = ref<any>({ 
  _id: "", 
  group_id: "", 
  title: "", 
  variable_type: "card_type", 
  type_value: "" 
});

const fetchOptions = async () => {
  try {
    const [cardRes, groupRes] = await Promise.all([
      apiService.get("common/cardtypes-filtered", { all: true, default: true }), // Changed to fetch all
      apiService.get("cms-process-flow/process-groups")
    ]);
    
    cardTypes.value = cardRes.data || [];
    processGroups.value = groupRes.data || groupRes || [];
    
    // Pre-selection logic for group_id
    if (processGroups.value.length > 0) {
      const defaultGroup = processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default')) || processGroups.value[0];
      if (defaultGroup) {
        newItemData.value.group_id = defaultGroup._id;
      }
    }
  } catch (err) {
    console.error("Failed to fetch options", err);
  }
};

const createFormFields = computed<FormField[]>(() => [
  { 
    key: "group_id", 
    label: "Select Group", 
    type: "select", 
    required: true, 
    options: processGroups.value.map(g => ({ _id: g._id, title: g.title }))
  },
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title" },
  { 
    key: "type_value", 
    label: "Card Type", 
    type: "select", 
    required: true, 
    options: cardTypes.value.map(c => ({ _id: c.title, title: c.title }))
  },
  { key: "variable_type", label: "Variable Type", type: "text", required: true },
]);

const editFormFields = computed<FormField[]>(() => [
  { 
    key: "group_id", 
    label: "Select Group", 
    type: "select", 
    required: true, 
    options: processGroups.value.map(g => ({ _id: g._id, title: g.title })),
    readonly: isViewMode.value 
  },
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter title", readonly: isViewMode.value },
  { 
    key: "type_value", 
    label: "Card Type", 
    type: "select", 
    required: true, 
    options: cardTypes.value.map(c => ({ _id: c.title, title: c.title })),
    readonly: isViewMode.value 
  },
  { key: "variable_type", label: "Variable Type", type: "text", required: true, readonly: isViewMode.value },
]);

const isCreateFormValid = computed(() => !newItemData.value.title.trim() || !newItemData.value.group_id || !newItemData.value.type_value);
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.group_id || !editingItemData.value.type_value);

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};
const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const res = await crud.createItem(newItemData.value);
  if (res) {
    isCreateModalOpen.value = false;
    newItemData.value = { 
      group_id: processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default'))?._id || processGroups.value[0]?._id || "", 
      title: "", 
      variable_type: "card_type", 
      type_value: "" 
    };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};
const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...payload } = editingItemData.value;
  try {
    crud.isLoading.value = true;
    const res = await apiService.put(API_RESOURCE, id, payload);
    if (res) {
      toast.success("Item updated successfully" as Content, { theme: "colored" });
      isEditModalOpen.value = false;
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error((err.response?.data?.message || err.message || "Failed to update.") as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};
const closeCreateModal = () => { 
  isCreateModalOpen.value = false; 
  newItemData.value = { 
    group_id: processGroups.value.find((g: any) => g.title?.toLowerCase().includes('default'))?._id || processGroups.value[0]?._id || "", 
    title: "", 
    variable_type: "card_type", 
    type_value: "" 
  }; 
};
const closeEditModal = () => { isEditModalOpen.value = false; isViewMode.value = false; editingItemData.value = { _id: "", group_id: "", title: "", variable_type: "card_type", type_value: "" }; };

const openFlowEditor = (row: any) => {
  selectedProcess.value = row;
  isFlowEditorOpen.value = true;
  console.log(selectedProcess.value, 'selectedProcess.value');
};


const closeFlowEditor = () => {
  isFlowEditorOpen.value = false;
  selectedProcess.value = null;
};

const saveFlow = async (flowObject: any) => {
  if (!selectedProcess.value?._id) return;
  
  try {
    crud.isLoading.value = true;
    const payload = {
      ...selectedProcess.value,
      raw_object: {
        ...(selectedProcess.value.raw_object || {}),
        flow_diagram: flowObject
      }
    };
    
    // Remove _id from payload for PUT
    const { _id, ...cleanPayload } = payload;
    
    const res = await apiService.put(API_RESOURCE, selectedProcess.value._id, cleanPayload);
    if (res) {
      toast.success("Workflow saved successfully" as Content, { theme: "colored" });
      closeFlowEditor();
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error("Failed to save workflow" as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([
    crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page }),
    fetchOptions()
  ]);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: title,
          pageTitle: "Process Group",
          icon: "fas fa-tasks",
          showAction: true,
          actionLabel: "Create New",
          modal: "createNew",
          disabled: !_unref(canCreate)('main')
        }, null, 8, ["disabled"]),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onView: handleView,
          permissionKey: "main"
        }, {
          "extra-actions": _withCtx(({ row }) => [
            _createElementVNode("button", {
              class: "btn btn-sm btn-outline-info",
              title: "Design Flow",
              onClick: ($event: any) => (openFlowEditor(row))
            }, [...(_cache[5] || (_cache[5] = [
              _createElementVNode("i", { class: "fas fa-project-diagram" }, null, -1)
            ]))], 8, _hoisted_3)
          ]),
          _: 1
        }, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_6)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_8),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && typeof p === 'number' && handlePageChange(p))
          }, _toDisplayString(p), 11, _hoisted_9))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_10)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      "save-button-disabled": isCreateFormValid.value,
      saveButtonText: "Create"
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: createFormFields.value,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "save-button-disabled"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      "show-save-button": !isViewMode.value,
      "save-button-disabled": isEditFormValid.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: editFormFields.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button", "save-button-disabled"]),
    (isFlowEditorOpen.value)
      ? (_openBlock(), _createBlock(ProcessFlowEditor, {
          key: 0,
          "process-title": selectedProcess.value?.title,
          "initial-data": selectedProcess.value?.raw_object?.flow_diagram,
          onClose: closeFlowEditor,
          onSave: saveFlow
        }, null, 8, ["process-title", "initial-data"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})